// Angular
import { Injectable } from '@angular/core';
import { catchError, tap } from 'rxjs/operators';
// Store
import { Selector, State, StateContext, Action } from '@ngxs/store';
// Actions
import {
	ClearGlobalConfigEnums,
	SetActionOptions,
	SetCMPOnHoldReasons,
	SetHandleMode,
	SetInspectionTypes,
	SetLastUpdate,
	SetOrigins,
	SetProductTypes,
	SetReceiveFromClientOptions,
	SetRetryActions,
	SetTrackingSystems,
	SetUAD36FieldTypes
} from './actions/global-configuration-enums.actions';
// Models
import { GlobalConfig_Enums_STATE_PATH, GlobalConfigurationEnumsStateModel } from './models/global-configuration-enums-state.models';
import { IdNameObjectType } from './models/global-configuration-state.model';
// Services
import { ProductsService } from '../products/service/products.service';
import { GenericIntegrationService } from '../service/generic-integration.service';

@State<GlobalConfigurationEnumsStateModel>({
	name: GlobalConfig_Enums_STATE_PATH,
	defaults: new GlobalConfigurationEnumsStateModel()
})
@Injectable()
export class GlobalConfigurationEnumsState {

	constructor(
		private productService: ProductsService,
		private genericIntegrationService: GenericIntegrationService
	){}

	/**
	 * Get the lastUpdate number value
	 * @param state
	 */
	@Selector()
	static getLastUpdate(state: GlobalConfigurationEnumsStateModel): number {
		return state.lastUpdate;
	}

	/**
	 * Get list of Tracking Systems
	 * @param state
	 */
	@Selector()
	static getTrackingSystems(state: GlobalConfigurationEnumsStateModel): IdNameObjectType[] {
		return state.trackingSystems;
	}

	/**
	 * Get list of handleMode
	 * @param state
	 */
	@Selector()
	static gethandleMode(state: GlobalConfigurationEnumsStateModel): Array<string> {
		return state.handleMode;
	}

	/**
	 * Get list of actions
	 * @param state
	 */
	@Selector()
	static getActionOptions(state: GlobalConfigurationEnumsStateModel): IdNameObjectType[] {
		return state.actions;
	}

	/**
	 * Get CMP on hold reasons options
	 * @param state
	 */
	@Selector()
	static getCMPOnHoldReasonsOptions(state: GlobalConfigurationEnumsStateModel): IdNameObjectType[] {
		return state.cmpOnHoldReasons;
	}

	/**
	 * Get list of receive from client options
	 * @param state
	 */
	@Selector()
	static getReceiveFromClientOptions(state: GlobalConfigurationEnumsStateModel): IdNameObjectType[] {
		return state.receiveFromClient;
	}

	/**
	 * Get list of receive from client options
	 * @param state
	 */
	@Selector()
	static getOrigins(state: GlobalConfigurationEnumsStateModel): IdNameObjectType[] {
		return state.origins;
	}

	/**
	 * Get list of receive from client options
	 * @param state
	 */
	@Selector()
	static getProductTypes(state: GlobalConfigurationEnumsStateModel): IdNameObjectType[] {
		return state.productTypes;
	}

	/**
	 * Get list of receive from client options
	 * @param state
	 */
	@Selector()
	static getInspectionTypes(state: GlobalConfigurationEnumsStateModel): IdNameObjectType[] {
		return state.inspectionTypes;
	}

	/**
	 * Get list of available retry actions
	 * @param state
	 */
	@Selector()
	static getRetryActions(state: GlobalConfigurationEnumsStateModel): any {
		return state.retryActions;
	}

	/**
	 * Get list of UAD 3.6 Field enums
	 * @param state
	 */
	@Selector()
	static getUAD36FieldTypes(state: GlobalConfigurationEnumsStateModel): any {
		return state.uad36FieldTypes;
	}

	/**
	 * Event to save in store tracking systems
	 * @param ctx
	 * @constructor
	 */
	@Action(SetLastUpdate)
	SetLastUpdate(ctx: StateContext<GlobalConfigurationEnumsStateModel>): void {
		ctx.patchState({
			lastUpdate: Date.now()
		});
	}

	/**
	 * Event to save in store tracking systems
	 * @param ctx
	 * @constructor
	 */
	@Action(SetTrackingSystems)
	SetTrackingSystems(ctx: StateContext<GlobalConfigurationEnumsStateModel>): any {
		return this.productService.getTrackingSystems().pipe(
			tap(result => {
				let items = [];
				if (result && result.length) {
					items = result.map(item => ({ id: item.id, name: item.title }));
				}

				ctx.patchState({
					trackingSystems: items
				});
			})
		);
	}

	/**
	 * Event to save in store Handle Mode
	 * @param ctx
	 * @constructor
	 */
	@Action(SetHandleMode)
	SetHandleMode(ctx: StateContext<GlobalConfigurationEnumsStateModel>): any {
		return this.productService.getHandleMode().pipe(
			tap(result => {
				let items = [];
				if (result && result.length) {
					items = result.map(item => item);
				}

				ctx.patchState({
					handleMode: items
				});
			})
		);
	}

	/**
	 * Set Enum Action list.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetActionOptions)
	SetActionOptions(ctx: StateContext<GlobalConfigurationEnumsStateModel>): any {
		return this.genericIntegrationService.getActionOptions()
			.pipe(
				tap(result => {
					const items = result && Object.keys(result).length
						? Object.entries(result).map(([, value]) => ({ id: value as string, name: value as string }))
						: [];

					ctx.patchState({
						actions: items
					});
				})
			);
	}

	/**
	 * Set Enum CMP On Hold Reasons.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetCMPOnHoldReasons)
	SetCMPOnHoldReasons(ctx: StateContext<GlobalConfigurationEnumsStateModel>): any {
		return this.genericIntegrationService.getCMPOnHoldReasons()
			.pipe(
				tap(result => {
					const items = result && Object.keys(result).length
						? Object.entries(result).map(([, value]) => ({ id: value as string, name: value as string }))
						: [];

					ctx.patchState({
						cmpOnHoldReasons: items
					});
				})
			);
	}

	/**
	 * Set Enum CMP On Hold Reasons.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetReceiveFromClientOptions)
	SetReceiveFromClientOptions(ctx: StateContext<GlobalConfigurationEnumsStateModel>): any {
		return this.genericIntegrationService.getReceiveFromClientOptions()
			.pipe(
				tap(result => {
					const items = result && Object.keys(result).length
						? Object.entries(result).map(([, value]) => ({ id: value as string, name: value as string }))
						: [];

					ctx.patchState({
						receiveFromClient: items
					});
				})
			);
	}

	/**
	 * Set Enum Origins.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetOrigins)
	SetOrigins(ctx: StateContext<GlobalConfigurationEnumsStateModel>): any {
		return this.productService.getOrigins()
			.pipe(
				tap(result => {
					let items = [];
					if (result && result.length) {
						items = result.map(item => item);
					}

					ctx.patchState({
						origins: items
					});
				})
			);
	}

	/**
	 * Set Enum ProductType.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetProductTypes)
	SetProductTypes(ctx: StateContext<GlobalConfigurationEnumsStateModel>): any {
		return this.productService.getProductTypes()
			.pipe(
				tap(result => {
					ctx.patchState({
						productTypes: result
					});
				})
			);
	}

	/**
	 * Set Enum InspectionType.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetInspectionTypes)
	SetInspectionTypes(ctx: StateContext<GlobalConfigurationEnumsStateModel>): any {
		return this.productService.getInspectionTypes()
			.pipe(
				tap(result => {
					ctx.patchState({
						inspectionTypes: result
					});
				})
			);
	}

	/**
	 * Set Enum InspectionType.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetRetryActions)
	SetRetryActions(ctx: StateContext<GlobalConfigurationEnumsStateModel>, { integrationName }: SetRetryActions): any {
		return new Promise(resolve => {
			const retryActions = {};
			this.genericIntegrationService.getRetryActions(integrationName).subscribe({
				next: result => {
					if (result) {
						Object.assign(retryActions, result);

						ctx.patchState({
							retryActions
						});
					}
					resolve(retryActions);
				},
				error: err => {
					ctx.patchState({
						retryActions
					});
					resolve(retryActions);
				}
			});
		});
	}

	/**
	 * Set Enum UAD36 Field.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetUAD36FieldTypes)
	SetUAD36FieldTypes(ctx: StateContext<GlobalConfigurationEnumsStateModel>): any {
		return this.productService.getUAD36FieldTypes()
			.pipe(
				tap(result => {
					ctx.patchState({
						uad36FieldTypes: result
					});
				})
			);
	}


	/**
	 * Clear enums from GlobalConfg state
	 * @param ctx
	 * @returns
	 */
	@Action(ClearGlobalConfigEnums)
	ClearGlobalConfigEnums(ctx: StateContext<GlobalConfigurationEnumsStateModel>): any {
		return ctx.patchState(new GlobalConfigurationEnumsStateModel());
	}
}
